import React, { useCallback, useEffect, useState, useMemo } from 'react';
import getStarredSpaces from '@core/spaceSelector/utils/getStarredSpaces';
import { useSelector, useDispatch } from 'react-redux';
import {
  changeSpace,
  toggleOmniSearch,
  toggleNotifications,
} from '@app/actions/navigation';
import { selectSpaceSelector } from '@core/spaceSelector/operations';
import {
  useTheme,
  AULA_AVATAR_BG_COLOUR,
  Icons,
  Avatar,
  Box,
  Separator,
  Typography,
  Text,
  Tooltip,
  Button,
  Badge,
  Link,
} from '@ublend-npm/aulaui-core';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useMediaQuery,
  Drawer as MuiDrawer,
  IconButton,
  MenuItem as MUIItem,
} from '@mui/material';
import { DARK } from '@core/constants/theme';
import { currentUser, currentUserId } from '@core/selectors/user';

import LaunchIcon from '@mui/icons-material/Launch';
import {
  getCurrentSpaceId,
  getCurrentSpaceName,
} from '@core/selectors/space/space.selectors';
import { fetchNotifications } from '@core/notifications/actions';
import { selectorsResponsive } from '@app/store/conversations';
import { conversationLatestMessage } from '@core/conversations/selectorsResponsive';
import StarSpaceOnboarding from './NoStarSpace';
import push from '../../../../utils/routing';
import logout from '../../../../utils/logout';
import {
  StyledAppBar,
  UserBarContainer,
  AulaLogoContainer,
  StyledMenuItem,
  StyledMenu,
  MUIStyledMenu,
  StyledNotificationsMenu,
} from './UserBar.styled';
import { selectors as notificationSelectors } from '../../../../store/notifications';
import NotificationsDialog from '../../NotificationsDialogResponsive';

const starredCategory = {
  label: 'Starred spaces',
  order: -1,
};

const SHOW_TOOLTIP_THRESHOLD = 17;

const UserBarWrapper = () => {
  const theme = useTheme();
  const { palette, breakpoints } = theme;
  const isDarkMode = palette?.mode === DARK;
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery(breakpoints.up('md'));

  // Get spaces from Redux state (same as SpaceSelector container)
  const spacesData = useSelector((state) => state.data.classRooms || {});
  const spaces = Object?.values(spacesData)
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    .map((s) => ({
      id: s.objectId,
      name: s.name,
      archived: s.archived,
      categories: s.categories,
      avatar: s.avatar,
      hasActivity: s.unread,
      isStarred: s.isStarred || false,
    }));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showAllSpaces, setShowAllSpaces] = useState(false);
  const [helpMenuAnchorEl, setHelpMenuAnchorEl] = useState(null);
  const currentSpaceId = useSelector(getCurrentSpaceId);
  const currentSpaceName = useSelector(getCurrentSpaceName);
  const user = useSelector(currentUser);
  const userId = useSelector(currentUserId);
  const unreadNotificationCount = useSelector(
    notificationSelectors.unreadNotificationCount
  );
  const lastNotification = useSelector(notificationSelectors.lastNotification);
  const [anchorElProfileMenu, setAnchorElProfileMenu] = useState(false);
  const [anchorElNotificationMenu, setAnchorElNotificationMenu] =
    useState(false);

  const { webAppFtOmniSearch202506 } = useFlags();
  const conversations = useSelector(selectorsResponsive.visibleConversations);
  const totalUnreadCount = useSelector((state) => {
    if (!userId) return null;
    return (
      conversations.filter((conversation) => {
        const isUnread =
          selectorsResponsive.isConversationUnreadBy &&
          selectorsResponsive.isConversationUnreadBy(
            state,
            conversation.id,
            userId
          );
        if (!isUnread) return false;
        // Only count as unread if the latest message was NOT sent by current user
        const latestMessage = conversationLatestMessage(state, conversation.id);
        return latestMessage?.writer !== userId;
      }).length || null
    );
  });
  const drawerItems = [
    {
      key: 'home',
      label: 'Home',
      linkUrl: '#',
      Icon: (
        <Icons.HomeOutlined
          fontSize="small"
          color="inherit"
          sx={{ width: 24, height: 24 }}
        />
      ),
    },
    {
      key: 'messages',
      label: 'Messages',
      linkUrl: isDesktop
        ? `/#/dashboard/${currentSpaceId}/messages/${conversations[0]?.id}`
        : `/#/dashboard/${currentSpaceId}/messages`,
      Icon: (
        <Icons.InboxOutlined
          fontSize="small"
          color="inherit"
          sx={{ width: 24, height: 24 }}
        />
      ),
      badgeCount: totalUnreadCount,
    },
  ];

  // Effect to ensure notifications are fetched and kept up-to-date
  useEffect(() => {
    // Fetch notifications immediately when component mounts
    dispatch(fetchNotifications());

    // Set up periodic fetching to ensure count stays updated
    // This acts as a fallback in case websocket updates are missed
    const intervalId = setInterval(() => {
      // Only fetch if the tab is visible to avoid unnecessary API calls
      if (!document.hidden) {
        dispatch(fetchNotifications());
      }
    }, 600000); // Fetch every 10 minutes

    // Also fetch when the tab becomes visible again
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        dispatch(fetchNotifications());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch]);

  // Effect to respond to new notifications from websocket
  useEffect(() => {
    if (lastNotification) {
      // When a new notification comes via websocket, refresh the notifications
      dispatch(fetchNotifications());
    }
  }, [lastNotification, dispatch]);

  const handleSearchClick = useCallback(() => {
    if (webAppFtOmniSearch202506) {
      push('/search');
    } else {
      dispatch(toggleOmniSearch());
    }
  }, [dispatch, webAppFtOmniSearch202506]);

  // Get starred spaces
  const starredSpaces = useMemo(() => getStarredSpaces(spaces), [spaces]);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setShowAllSpaces(false); // Reset to starred spaces view when drawer closes
  }, []);

  const handleMenuClick = useCallback(() => {
    setDrawerOpen((prev) => !prev);
  }, []);

  const handleNotificationsOpen = useCallback(
    (event) => {
      setAnchorElNotificationMenu(event.target);
      dispatch(toggleNotifications());
    },
    [dispatch]
  );

  const handleNotificationsClose = useCallback(() => {
    setAnchorElNotificationMenu(null);
    dispatch(toggleNotifications());
  }, [dispatch]);

  // const handleAccountClick = useCallback(() => {
  //   push('/profile');
  //   handleProfileMenuClose();
  // },[])

  const handleSpaceSelect = useCallback(
    async (spaceId) => {
      try {
        // Close the drawer immediately and reset view
        closeDrawer();

        // Navigate to the space (same logic as SpaceSelector)
        dispatch(changeSpace(spaceId)());

        // Track the selection (same logic as SpaceSelector)
        dispatch(
          selectSpaceSelector(
            spaceId,
            false, // showArchived
            'ascending', // sort
            starredCategory.label, // category
            null // subCategory
          )
        );
      } catch (error) {
        console.error('Error navigating to space:', error);
        closeDrawer();
      }
    },
    [dispatch, closeDrawer]
  );

  const handleHelpClick = useCallback((event) => {
    setHelpMenuAnchorEl(event.currentTarget);
  }, []);

  const closeHelpMenu = useCallback(() => {
    setHelpMenuAnchorEl(null);
  }, []);

  const handleProfileClick = useCallback((event) => {
    setAnchorElProfileMenu(event.target);
  }, []);

  const handleHelpWithAulaClick = useCallback(() => {
    // Close the help menu first
    closeHelpMenu();
  }, [dispatch, closeHelpMenu]);

  const handleProfileMenuClose = useCallback(() => {
    setAnchorElProfileMenu(null);
  }, []);

  const handleAccountClick = () => {
    handleProfileMenuClose();
  };

  // const handleProfileClick = useCallback((event) => {
  //   setAnchorEl(event.target);
  // }, []);

  const handleSessionLogout = useCallback(async () => {
    await logout({ returnAfterLogin: true, makeLogoutApiCall: true });
  });

  useEffect(() => {
    const contentContainer = document.getElementById('content-container');
    if (contentContainer) {
      const contentElement = contentContainer.querySelector('.content');
      if (contentElement) {
        contentElement.style.paddingTop = '60px';
        contentElement.style.background = palette.background.default;
      }
    }
    return () => {
      // Clean up when component unmounts
      if (contentContainer) {
        const contentElement = contentContainer.querySelector('.content');
        if (contentElement) {
          contentElement.style.paddingTop = '0';
        }
      }
    };
  }, []);

  // Focus management for drawer accessibility
  useEffect(() => {
    let timer = null;
    if (drawerOpen) {
      // Small delay to ensure drawer is fully rendered
      timer = setTimeout(() => {
        const closeButton = document.querySelector(
          '[aria-label="Close navigation drawer"]'
        );
        if (closeButton) {
          closeButton.focus();
        }
      }, 100);
    }
    return () => !!timer && clearTimeout(timer);
  }, [drawerOpen]);

  // Drawer Spaces --------------------------

  // ==========================
  const RenderStarSpacesAndAllSpacer = () => {
    const emptyMessage = showAllSpaces ? (
      'No spaces available'
    ) : (
      <StarSpaceOnboarding />
    );

    if (starredSpaces.length === 0) {
      return <Box>{emptyMessage}</Box>;
    }
    return starredSpaces.map((space) => {
      // List Item
      return (
        <Box
          key={`all-spaces-${space?.id}`}
          component="a"
          tabIndex={0}
          role="listitem"
          aria-label={`You are currently on ${space.name} space. To switch in it, press Control-Option-Space`}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding="0.56rem 1rem"
          color="inherit"
          width="100%"
          onClick={() => {
            handleSpaceSelect(space.id, space.name);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSpaceSelect(space.id, space.name);
            }
          }}
          sx={{
            minHeight: '48px',
            cursor: 'pointer',
            backgroundColor:
              currentSpaceId === space.id
                ? palette.action.selected
                : 'transparent',
            width: 'calc(100% - 8px)',
            overflow: 'hidden',
            position: 'relative',
            ...(isDesktop && {
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }),
            '&:active': {
              ...(!isDesktop && {
                backgroundColor: 'action.selected',
                transform: 'scale(0.98)',
              }),
            },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap="1rem"
            flex={1}
            pr="0.68rem"
            sx={{
              '& .MuiBadge-root .MuiBadge-badge': {
                boxShadow: '0 0 0 0.5px white',
                bottom: '2px',
                right: '2px',
                width: '1px',
                height: '8px',
                borderRadius: '50%',
              },
            }}
          >
            <Avatar
              kind="circular"
              sizes="10px"
              src={space.avatar}
              fallbackText={space.name?.substring(0, 2).toUpperCase()}
              showBadge={space?.hasActivity}
              badgeColor={palette.primary.main}
              sx={{
                bgcolor: '#5d536c',
                color: '#fff',
                height: '1.5rem',
                width: '1.5rem',
                fontSize: '0.75rem',
              }}
            />
            {space.name.length >= SHOW_TOOLTIP_THRESHOLD && isDesktop ? (
              <Tooltip title={space.name} placement="right">
                <Box>
                  <Text maxLines={2}>{space.name}</Text>
                </Box>
              </Tooltip>
            ) : (
              <Text maxLines={2}>{space.name}</Text>
            )}
          </Box>
          {/* Only show star icon when in starred spaces view */}
          {!showAllSpaces && (
            <Icons.Star
              fontSize="small"
              color="primary"
              style={{
                width: 20,
                height: 20,
                p: '5px',
              }}
            />
          )}
        </Box>
      );
    });
  };

  const appBarStyle = {
    backgroundColor: palette.background.paper,
    color: isDarkMode ? palette.common.white : palette.common.black,
    backgroundImage: 'none',
    boxShadow: `1px 1px 0px ${
      isDarkMode
        ? `${palette.common.white}fff30`
        : `${palette.common.black}00030`
    }`,
  };

  const fallbackText = `${
    user?.firstName ? user.firstName.charAt(0).toUpperCase() : 'U'
  }${user?.lastName ? user.lastName.charAt(0).toUpperCase() : ''}`.substring(
    0,
    2
  );

  const handleViewAllSpace = () => {
    // If no current space ID, use the first available space or fallback to a general spaces route
    const spaceId =
      currentSpaceId || (spaces.length > 0 ? spaces[0].id : 'default');
    push(`/dashboard/${spaceId}/spaces`);
    closeDrawer();
  };
  return (
    <>
      <UserBarContainer
        role="region"
        aria-label="user bar"
        className="responsive-user-bar"
      >
        <StyledAppBar
          position="fixed"
          logo={<Icons.Menu />}
          onClickLogo={handleMenuClick}
          title={
            <AulaLogoContainer>
              <Icons.AulaLogo
                primaryColor={palette.primary.main}
                secondaryColor={
                  isDarkMode ? palette.common.white : palette.common.black
                }
                fontSize={6}
              />
              {currentSpaceName && (
                <Box
                  component="span"
                  sx={{
                    marginLeft: '16px',
                    fontSize: '1.25rem',
                    fontWeight: 500,
                    color: palette.text.primary,
                    display: { xs: 'none', sm: 'inline' },
                    maxWidth: { sm: '260px', md: '500px', lg: '800px' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  | &nbsp; {currentSpaceName}
                </Box>
              )}
            </AulaLogoContainer>
          }
          searchBox={false}
          badgeIconI={<Icons.Search />}
          badgeIconICount={0}
          onClickBadgeIconI={handleSearchClick}
          badgeIconII={<Icons.NotificationsOutlined />}
          badgeIconIICount={unreadNotificationCount}
          onClickBadgeIconII={handleNotificationsOpen}
          badgeIconIII={
            <Avatar
              src={user?.avatar}
              fallbackText={fallbackText}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: AULA_AVATAR_BG_COLOUR,
                color: palette.common.white,
              }}
              onClick={handleProfileClick}
            />
          }
          badgeIconIIICount={0}
          onClickBadgeIconIII={handleProfileClick}
          style={appBarStyle}
        />
        {/* App Bar Ends --------------------------- */}
        {/* App Bar Profile Menu -----------------------------------*/}
        <StyledMenu
          isDarkMode={isDarkMode}
          anchorEl={anchorElProfileMenu}
          open={Boolean(anchorElProfileMenu)}
          onClose={handleProfileMenuClose}
        >
          <Box display="flex" alignItems="center" gap={2} px={2} py={1}>
            <Avatar
              src={user?.avatar}
              fallbackText={fallbackText}
              sx={{
                width: 40,
                height: 40,
                backgroundColor: AULA_AVATAR_BG_COLOUR,
                color: palette.common.white,
              }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </Box>
          <Separator sx={{ marginBottom: '8px' }} />
          <Link
            href={`/#/dashboard/${currentSpaceId}/profile`}
            sx={{ textDecoration: 'none', color: 'inherit' }}
          >
            <StyledMenuItem
              icon={<Icons.Person2Outlined />}
              label="Account"
              onClick={handleAccountClick}
            />
          </Link>
          <Link
            href={`/#/dashboard/${currentSpaceId}/profile/notifications`}
            sx={{ textDecoration: 'none', color: 'inherit' }}
          >
            <StyledMenuItem
              icon={<Icons.SettingsOutlined />}
              label="Settings"
              onClick={() => handleProfileMenuClose()}
            />
          </Link>
          <StyledMenuItem
            icon={<Icons.LogoutOutlined />}
            label="Sign out"
            onClick={handleSessionLogout}
          />
        </StyledMenu>

        {/* Notifications Dialog */}
        <StyledNotificationsMenu
          isDarkMode={isDarkMode}
          anchorEl={anchorElNotificationMenu}
          open={Boolean(anchorElNotificationMenu)}
          onClose={handleNotificationsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <NotificationsDialog onClose={handleNotificationsClose} />
        </StyledNotificationsMenu>

        {/* App Bar Profile Ends ------------------------------------------ */}
        {/* Drawer  Starts ---------------------------------------- */}
        <MuiDrawer
          open={drawerOpen}
          onClose={closeDrawer}
          anchor="left"
          aria-label="Navigation drawer"
          role="navigation"
          ModalProps={{
            'aria-labelledby': 'drawer-title',
            'aria-describedby': 'drawer-description',
            keepMounted: false,
          }}
          PaperProps={{
            'aria-modal': 'true',
            role: 'dialog',
          }}
        >
          <Box
            sx={{
              width: 320,
              minWidth: 320,
              maxWidth: 320,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              backgroundColor: isDarkMode ? '#363636' : 'transparent',
              color: 'inherit',
            }}
            role="navigation"
            aria-label="Main navigation menu"
          >
            {/* Hidden title and description for screen readers */}
            <div
              id="drawer-title"
              style={{ position: 'absolute', left: '-10000px' }}
            >
              Navigation Menu
            </div>
            <div
              id="drawer-description"
              style={{ position: 'absolute', left: '-10000px' }}
            >
              Main navigation drawer containing spaces, messages, and user
              options
            </div>
            {/* Top Container */}
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                py: '1rem',
              }}
            >
              {/* Close Button */}
              <IconButton
                onClick={closeDrawer}
                size="small"
                aria-label="Close navigation drawer"
                sx={{
                  alignSelf: 'flex-end',
                  width: '28px',
                  height: '28px',
                  m: '0.5rem 1rem',
                  borderRadius: '50%',
                  '&:hover': {
                    ...(isDesktop
                      ? {
                          backgroundColor: 'action.hover',
                          boxShadow: 'none',
                        }
                      : {
                          backgroundColor: 'transparent !important',
                        }),
                  },
                  '&:active': {
                    ...(!isDesktop && {
                      backgroundColor: 'action.selected',
                      transform: 'scale(0.95)',
                    }),
                  },
                }}
              >
                <Icons.CloseOutlined color="inherit" />
              </IconButton>

              {/* HOME and MESSAGES */}
              <Box role="list" aria-label="Main navigation items">
                {drawerItems.map(
                  ({ key, label, Icon, badgeCount, linkUrl }) => (
                    <Link
                      sx={{ textDecoration: 'none', color: 'inherit' }}
                      href={linkUrl}
                    >
                      <Box
                        key={key}
                        component="button"
                        tabIndex={0}
                        role="listitem"
                        aria-label={`You are currently on ${label} tab. To click this tab, press Control-Option-Space`}
                        display="flex"
                        alignItems="center"
                        gap="0.75rem"
                        padding="0.56rem 1rem"
                        fontWeight={400}
                        sx={{
                          cursor: 'pointer',
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: 'inherit',
                          width: '100%',
                          textAlign: 'left',

                          ...(isDesktop && {
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            },
                          }),
                          '&:active': {
                            ...(!isDesktop && {
                              backgroundColor: 'action.selected',
                              transform: 'scale(0.98)',
                            }),
                          },
                        }}
                        onClick={() => {
                          // Handle navigation based on key
                          if (key === 'home') {
                            push('/dashboard');
                          } else if (key === 'messages') {
                            push('/messages');
                          }
                          closeDrawer();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            // Handle navigation based on key
                            if (key === 'home') {
                              push('/dashboard');
                            } else if (key === 'messages') {
                              push('/messages');
                            }
                            closeDrawer();
                          }
                        }}
                      >
                        <IconButton
                          sx={{ p: 0, fontSize: '1rem' }}
                          tabIndex={-1}
                          aria-hidden="true"
                        >
                          {Icon}
                        </IconButton>
                        <Text>{label}</Text>
                        {badgeCount && (
                          <Badge
                            content={badgeCount}
                            variant="standard"
                            color="primary"
                            sx={{ ml: 'auto', right: '12px' }}
                            aria-label={`${badgeCount} unread items`}
                          />
                        )}
                      </Box>
                    </Link>
                  )
                )}
              </Box>
            </Box>

            <Separator sx={{ marginBottom: '1rem', marginTop: '.5rem' }} />

            {/* Current Space Section */}
            {currentSpaceId && (
              <Box mb={2}>
                <Box
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    marginBottom: 1,
                    paddingLeft: 2,
                    paddingRight: 2,
                    color: 'text.primary',
                  }}
                >
                  <Text>Current space</Text>
                </Box>
                <Box
                  component="a"
                  tabIndex={0}
                  role="listitem"
                  aria-label={`You are currently on ${currentSpaceName} space. To go to it's space navigation page, press Control-Option-Space`}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  padding="0.56rem 1rem"
                  color="inherit"
                  width="100%"
                  onClick={() => {
                    push(`/dashboard/${currentSpaceId}/myspacenav`);
                    closeDrawer();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      push(`/dashboard/${currentSpaceId}/myspacenav`);
                      closeDrawer();
                    }
                  }}
                  sx={{
                    minHeight: '48px',
                    cursor: 'pointer',
                    backgroundColor: 'action.selected',
                    width: '100%',
                    overflow: 'hidden',
                    position: 'relative',
                    ...(isDesktop && {
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }),
                    '&:active': {
                      ...(!isDesktop && {
                        backgroundColor: 'action.selected',
                        transform: 'scale(0.98)',
                      }),
                    },
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="1rem"
                    flex={1}
                    pr="0.68rem"
                    sx={{
                      '& .MuiBadge-root .MuiBadge-badge': {
                        boxShadow: '0 0 0 0.5px white',
                        bottom: '2px',
                        right: '2px',
                        width: '1px',
                        height: '8px',
                        borderRadius: '50%',
                      },
                    }}
                  >
                    <Avatar
                      kind="circular"
                      sizes="10px"
                      src={spaces.find((s) => s.id === currentSpaceId)?.avatar}
                      fallbackText={currentSpaceName
                        ?.substring(0, 2)
                        .toUpperCase()}
                      showBadge={
                        spaces.find((s) => s.id === currentSpaceId)?.hasActivity
                      }
                      badgeColor={palette.primary.main}
                      sx={{
                        bgcolor: '#5d536c',
                        color: '#fff',
                        height: '1.5rem',
                        width: '1.5rem',
                        fontSize: '0.75rem',
                      }}
                    />
                    {currentSpaceName?.length >= SHOW_TOOLTIP_THRESHOLD &&
                    isDesktop ? (
                      <Tooltip title={currentSpaceName} placement="right">
                        <Box>
                          <Text maxLines={2}>{currentSpaceName}</Text>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Text maxLines={2}>{currentSpaceName}</Text>
                    )}
                  </Box>
                  {spaces.find((s) => s.id === currentSpaceId)?.isStarred && (
                    <Icons.Star
                      fontSize="small"
                      color="primary"
                      style={{
                        width: 20,
                        height: 20,
                        p: '5px',
                      }}
                    />
                  )}
                </Box>

                <Separator sx={{ my: '1rem' }} />
              </Box>
            )}

            {/* Spaces Section, Middle Container */}
            <Box
              style={{ flexGrow: 1 }}
              role="region"
              aria-label="Starred spaces section"
            >
              <Box
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  marginBottom: 8,
                  paddingLeft: 16,
                  paddingRight: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: isDarkMode ? 'rgba(255, 255, 255, 1)' : 'inherit',
                }}
                role="heading"
                aria-level="2"
              >
                <Text>Starred spaces </Text>
              </Box>

              {/* Scrollable spaces list container */}
              <Box
                style={{
                  maxHeight: '228px',
                  overflowY: '228px' ? 'auto' : 'visible',
                  backgroundColor: 'transparent',
                  position: 'relative',
                }}
                sx={{
                  // Hide scrollbar hover effects
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent !important',
                    },
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.3) !important',
                    },
                  },
                  // Firefox scrollbar
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(0,0,0,0.2) transparent',
                }}
                className="scrollable-spaces-list"
                role="list"
                aria-label={`Starred spaces, ${starredSpaces.length} items`}
                aria-describedby="starred-spaces-description"
              >
                <RenderStarSpacesAndAllSpacer />
              </Box>
              <div id="starred-spaces-description" style={{ display: 'none' }}>
                List of your starred spaces. Use arrow keys to navigate and
                Enter to select.
              </div>
              {/* Show All Spaces */}

              <Box
                display="flex"
                justifyContent="center"
                p="0.5rem 1rem"
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  backgroundColor: 'inherit',
                }}
              >
                <Button
                  kind="secondary"
                  onClick={handleViewAllSpace}
                  fullWidth
                  aria-label="View all spaces - navigate to complete spaces list"
                  sx={{
                    textTransform: 'none',
                    marginTop: '0.2rem',
                    '&:hover': {
                      border: '1px solid !important',
                    },
                  }}
                >
                  View all spaces
                </Button>
              </Box>
              <Separator sx={{ my: '0.5rem' }} />
            </Box>

            {/* Help, Last Container */}
            <Box
              id="help-button"
              component="button"
              onClick={handleHelpClick}
              mb="0.5rem"
              p="0.5rem 1rem"
              fontSize="1rem"
              alignItems="center"
              display="flex"
              gap="0.75rem"
              tabIndex={0}
              role="button"
              aria-label="Help - Open help menu"
              aria-expanded={Boolean(helpMenuAnchorEl)}
              aria-haspopup="menu"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleHelpClick(e);
                }
              }}
              sx={{
                cursor: 'pointer',
                backgroundColor: helpMenuAnchorEl
                  ? 'action.selected'
                  : 'transparent',
                border: 'none',
                color: 'inherit',

                ...(isDesktop && {
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }),
                '&:active': {
                  ...(!isDesktop && {
                    backgroundColor: 'action.selected',
                    transform: 'scale(0.98)',
                  }),
                },
              }}
            >
              <Icons.HelpOutline
                fontSize="small"
                color="inherit"
                sx={{
                  width: 24,
                  height: 24,
                  cursor: 'pointer',
                }}
                aria-hidden="true"
              />
              Help
            </Box>
          </Box>
        </MuiDrawer>

        {/* Help Menu */}
        <MUIStyledMenu
          id="help-menu"
          anchorEl={helpMenuAnchorEl}
          open={Boolean(helpMenuAnchorEl)}
          onClose={closeHelpMenu}
          slotProps={{
            list: {
              'aria-labelledby': 'help-button',
              'aria-label': 'Help menu options',
              role: 'menu',
            },
          }}
        >
          <MUIItem
            onClick={handleHelpWithAulaClick}
            disableRipple={isDesktop}
            tabIndex={-4}
            role="menuitem"
            aria-label="Help with Aula - Open help support"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleHelpWithAulaClick();
              }
            }}
            sx={{
              fontFamily: 'Roboto, sans-serif',
              color: isDarkMode ? '#FFFFFF' : '#000000DE',
              width: '280px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0px',
              cursor: 'default',
              '&:hover': {
                ...(isDesktop
                  ? {
                      backgroundColor: 'action.hover',
                    }
                  : {
                      backgroundColor: 'transparent !important',
                    }),
              },
              '&:active': {
                ...(!isDesktop && {
                  backgroundColor: 'action.selected',
                  transform: 'scale(0.98)',
                }),
              },
            }}
          >
            <Box
              sx={{
                width: '280px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingLeft: '16px',
              }}
            >
              <span>Help with Aula</span>
            </Box>
          </MUIItem>
          <MUIItem
            onClick={closeHelpMenu}
            disableRipple={isDesktop}
            tabIndex={-3}
            component="a"
            href="https://www.aula.education/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            aria-label="Terms of Service - Opens in new tab"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                window.open(
                  'https://www.aula.education/terms-of-service',
                  '_blank',
                  'noopener,noreferrer'
                );
                closeHelpMenu();
              }
            }}
            sx={{
              textDecoration: 'none',
              fontFamily: 'Roboto, sans-serif',
              color: isDarkMode ? '#FFFFFF' : '#000000DE',
              width: '280px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0px',
              '&:hover': {
                ...(isDesktop
                  ? {
                      backgroundColor: 'action.hover',
                    }
                  : {
                      backgroundColor: 'transparent !important',
                    }),
              },
              '&:active': {
                ...(!isDesktop && {
                  backgroundColor: 'action.selected',
                  transform: 'scale(0.98)',
                }),
              },
            }}
          >
            <Box
              sx={{
                width: '248px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>Terms of Service</span>
              <LaunchIcon
                sx={{
                  fontSize: 24,
                  color: isDarkMode ? '#FFFFFFB2' : '#00000099',
                }}
                aria-hidden="true"
              />
            </Box>
          </MUIItem>
          <MUIItem
            onClick={closeHelpMenu}
            disableRipple={isDesktop}
            tabIndex={-2}
            component="a"
            href="https://www.aula.education/privacy"
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            aria-label="Privacy Policy - Opens in new tab"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                window.open(
                  'https://www.aula.education/privacy',
                  '_blank',
                  'noopener,noreferrer'
                );
                closeHelpMenu();
              }
            }}
            sx={{
              textDecoration: 'none',
              fontFamily: 'Roboto, sans-serif',
              color: isDarkMode ? '#FFFFFF' : '#000000DE',
              width: '280px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0px',
              '&:hover': {
                ...(isDesktop
                  ? {
                      backgroundColor: 'action.hover',
                    }
                  : {
                      backgroundColor: 'transparent !important',
                    }),
              },
              '&:active': {
                ...(!isDesktop && {
                  backgroundColor: 'action.selected',
                  transform: 'scale(0.98)',
                }),
              },
            }}
          >
            <Box
              sx={{
                width: '248px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span>Privacy Policy</span>
              <LaunchIcon
                sx={{
                  fontSize: 24,
                  color: isDarkMode ? '#FFFFFFB2' : '#00000099',
                }}
                aria-hidden="true"
              />
            </Box>
          </MUIItem>
          <MUIItem
            onClick={closeHelpMenu}
            disableRipple={isDesktop}
            tabIndex={-1}
            component="a"
            href="https://portal.aula.education/tabs/3-launched"
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            aria-label="Aula's product portal - Learn about new and upcoming features - Opens in new tab"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                window.open(
                  'https://portal.aula.education/tabs/3-launched',
                  '_blank',
                  'noopener,noreferrer'
                );
                closeHelpMenu();
              }
            }}
            sx={{
              textDecoration: 'none',
              fontFamily: 'Roboto, sans-serif',
              color: isDarkMode ? '#FFFFFF' : '#000000DE',
              width: '280px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0px',
              '&:hover': {
                ...(isDesktop
                  ? {
                      backgroundColor: 'action.hover',
                    }
                  : {
                      backgroundColor: 'transparent !important',
                    }),
              },
              '&:active': {
                ...(!isDesktop && {
                  backgroundColor: 'action.selected',
                  transform: 'scale(0.98)',
                }),
              },
            }}
          >
            <Box
              sx={{
                width: '248px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span width="224px" height="72px" style={{ textWrap: 'auto' }}>
                Aula's product portal <br />
                <span
                  style={{
                    fontSize: '0.875rem',
                    color: isDarkMode ? '#FFFFFFB2' : '#00000099',
                  }}
                  aria-hidden="true"
                >
                  Learn about new and upcoming features
                </span>
              </span>
              <LaunchIcon
                sx={{
                  fontSize: 24,
                  color: isDarkMode ? '#FFFFFFB2' : '#00000099',
                }}
                aria-hidden="true"
              />
            </Box>
          </MUIItem>
        </MUIStyledMenu>
        {/* Drawer  Ends ---------------------------------- */}
      </UserBarContainer>
    </>
  );
};

export default React.memo(UserBarWrapper);
